function defaultEqualityCheck<T>(a: T, b: T): boolean {
  return a === b;
}

function areArgumentsShallowlyEqual(equalityCheck: <T>(a: T, b: T) => boolean, prev: any, next: any): boolean {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const length = prev.length;
  for (let i = 0; i < length; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

const _memoize = <F extends { apply: any }>(
  f: F,
  equalityCheck = defaultEqualityCheck,
  lastArgs: any,
  lastResult: any,
): F => {
  // we reference arguments instead of spreading them for performance reasons
  function memoizedFunction() {
    // tslint:disable-line
    if (!areArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)) {
      // apply arguments instead of spreading for performance.
      lastArgs = arguments;
      lastResult = f.apply(null, arguments);
    }

    return lastResult;
  }

  (memoizedFunction as any).isMemoizedFunction = true;
  (memoizedFunction as any).clone = () => {
    return _memoize(f, equalityCheck, lastArgs, lastResult);
  };

  return memoizedFunction as any;
};

export const memoize = <F extends { apply: any }>(f: F, equalityCheck = defaultEqualityCheck): F => {
  return _memoize(f, equalityCheck, null, null);
};

export const isMemoizedFunction = (o: any, k: string): boolean => {
  const { value } = Object.getOwnPropertyDescriptor(o, k) as PropertyDescriptor;
  return typeof value === "function" && value.isMemoizedFunction;
};

export const cloneMemoizedFunction = <T>(x: T): T => {
  return (x as any).clone();
};
